import React from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import ContainerBox from 'components/ContainerBox';
import { useSelector } from 'react-redux';
import * as ActiveProductSelectors from 'selectors/ActiveProductSelectors';
import * as StockSelectors from 'selectors/StockSelectors';
import { SkidataPackageProductApi } from 'common/types';
import { useTranslation } from 'services/localization';
import { uniq, uniqBy } from 'lodash';

const PackageTicketProducts = () => {
	const classes = useStyles();
	const { t, getTranslation } = useTranslation();

	const product = useSelector(
		ActiveProductSelectors.productData,
	) as SkidataPackageProductApi | null;

	const liftTicketSegments = useSelector(StockSelectors.liftTicketSegments);
	const liftTicketProducts = useSelector(StockSelectors.liftTicketProducts);

	if (!product) return null;

	const externalIds = uniq(product.packageItems?.map((item) => item.externalId) ?? []);
	const uniqueSegments = uniqBy(product.packageItems, (item) => item.externalSegmentId);

	const getSegmentName = (segmentId: string) => {
		const segment = liftTicketSegments?.find((segment) => segment.externalSegmentId === segmentId);

		if (!segment) return segmentId;

		return segment.name ? getTranslation(segment.name) : segment?.externalSegmentName;
	};

	const getTicketName = (externalId: string) => {
		const ticket = liftTicketProducts?.find((ticket) => ticket.externalId === externalId);

		if (!ticket) return externalId;

		return ticket.name ? getTranslation(ticket.name) : ticket.externalName;
	};

	return (
		<ContainerBox>
			<Typography variant="body2">
				{t('PackageTicketProducts.packageRules', 'Package rules')}
			</Typography>
			<Box className={classes.row}>
				<Typography variant="body2" color="textSecondary" className={classes.rowLeft}>
					{t('PackageTicketProducts.validTicketTypes', 'Valid ticket types')}
				</Typography>
				<Box className={classes.rowRight}>
					<Typography variant="body1" align="right">
						{externalIds.map((id) => getTicketName(id)).join(', ')}
					</Typography>
				</Box>
			</Box>
			<Divider />
			<Box className={classes.row}>
				<Typography variant="body2" color="textSecondary" className={classes.rowLeft}>
					{t('PackageTicketProducts.minTicketsRequired', 'Min. tickets required')}
				</Typography>
				<Box className={classes.rowRight}>
					<Typography variant="body1" align="right">
						{product.minItemQuantity}
					</Typography>
				</Box>
			</Box>
			<Divider />
			<Box className={classes.row}>
				<Typography variant="body2" color="textSecondary" className={classes.rowLeft}>
					{t('PackageTicketProducts.segmentRules', 'Segment rules')}
				</Typography>
				<Box className={classes.rowRight}>
					{uniqueSegments.map((item, idx) => (
						<Typography key={idx} variant="body1" align="right">
							{getSegmentName(item.externalSegmentId)}
							{` (min: ${item.quantity.min}, max: ${item.quantity.max})`}
						</Typography>
					))}
				</Box>
			</Box>
		</ContainerBox>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		row: {
			display: 'flex',
			flexDirection: 'column',
			margin: theme.spacing(2, 0),
			[theme.breakpoints.up('sm')]: {
				flexDirection: 'row',
				justifyContent: 'space-between',
			},
		},
		rowLeft: {
			marginRight: theme.spacing(2),
		},
		rowRight: {
			[theme.breakpoints.up('sm')]: {
				maxWidth: '50%',
			},
		},
	}),
);

export default PackageTicketProducts;
